/**
 * N1 · Competência 6 (Estatística) · task-model H1.34.K01
 *
 *   "Ler um gráfico de setores pelo ângulo do setor."
 *
 * O gráfico de setores tem uma propriedade que nenhum outro tem: a parte se lê no
 * **ângulo**. Um item que só escreve as porcentagens ao lado de cada fatia usa o
 * desenho como enfeite — a conta seria a mesma numa lista. Aqui o número que aparece na
 * figura é o ângulo, e chegar à quantidade exige o passo que o gráfico de setores
 * cobra: a fatia está para 360° como a parte está para o total.
 *
 * OS ERROS SÃO TODOS DE PROPORÇÃO ERRADA, e cada um nomeia uma troca específica do
 * denominador ou do papel dos números: tomar o ângulo por porcentagem, usar 180 no
 * lugar de 360 (meia-volta é meio), usar 100 no lugar de 360, responder o ângulo, ou
 * responder o que sobra. São os enganos que um gráfico de setores produz, e não erros
 * de conta genéricos.
 */

import { formatBR } from '../../core/num.ts';
import { conta, osN, quantos, type NomeId } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { describeSetores, setores, type Fatia } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  SET_ANGULO_COMO_PCT,
  SET_COMPLEMENTO,
  SET_DIVIDE_POR_100,
  SET_FATIA_VIZINHA,
  SET_MEIA_VOLTA,
  SET_RESPONDE_O_ANGULO,
  SET_TOTAL_POR_FATIA,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

interface Contexto {
  readonly texto: string;
  readonly titulo: string;
  /**
   * QUEM É CONTADO — uma chave do léxico, não um texto.
   *
   * Havia dois campos aqui, `unidade` e `pergunta`, e os três contextos escreviam a
   * MESMA palavra nos dois. A duplicação não era redundância inofensiva: era o que
   * permitia `Quantos ${ctx.pergunta}`, que imprimia "Quantos consultas escolheram
   * Rotina?" no comando — a frase mais visível do item. Com a chave no lugar do texto,
   * `quantos('consulta')` sabe que a palavra é feminina e escreve "Quantas consultas".
   */
  readonly conta: NomeId;
  readonly nomes: readonly string[];
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Uma escola perguntou a todos os alunos qual meio de transporte usam para chegar às aulas.',
    titulo: 'Como os alunos chegam à escola',
    conta: 'aluno',
    nomes: ['A pé', 'Ônibus', 'Carro', 'Bicicleta'],
  },
  {
    texto: 'Um posto de saúde registrou o motivo das consultas marcadas num mês.',
    titulo: 'Motivo das consultas',
    conta: 'consulta',
    nomes: ['Rotina', 'Vacina', 'Exame', 'Retorno'],
  },
  {
    texto: 'Uma pesquisa perguntou a moradores de um bairro qual serviço público eles mais usam.',
    titulo: 'Serviço mais usado',
    conta: 'morador',
    nomes: ['Transporte', 'Saúde', 'Escola', 'Praça'],
  },
];

export interface Params {
  readonly contexto: number;
  /** O total de pessoas — o que o círculo inteiro representa. */
  readonly total: number;
  /** Quantas pessoas em cada fatia, na ordem dos nomes. */
  readonly partes: readonly number[];
  /** A fatia sobre a qual se pergunta. */
  readonly alvo: number;
}

const parte = (p: Params, i: number): number => p.partes[i] as number;
const anguloDe = (p: Params, i: number): number => (parte(p, i) / p.total) * 360;

const n0 = (x: number): string => formatBR(Math.round(x), 0);

/** A palavra contada nesta variante. */
const unidadeDe = (p: Params): NomeId => (CONTEXTOS[p.contexto] as Contexto).conta;

/**
 * "45 ÷ 360 ≈ 0,13", mas "90 ÷ 360 = 0,25".
 *
 * A fração de círculo é escrita com duas casas, e duas casas não dão conta de 45/360,
 * que é 0,125. Com o sinal de igual, o item afirmava ao aluno que 45 ÷ 360 é 0,13 — num
 * item cuja resolução inteira depende de essa divisão estar certa.
 */
const fracaoDoCirculo = (ang: number): string => {
  const exato = ang / 360;
  const duasCasas = Math.round(exato * 100) / 100;
  const sinal = Math.abs(exato - duasCasas) < 1e-9 ? '=' : '≈';
  return `${n0(ang)} ÷ 360 ${sinal} ${formatBR(duasCasas, 2)}`;
};

function drawParams(rng: Rng): Params {
  // O total é múltiplo de 24 — 360/15, o passo dos ângulos — para que toda fatia caia
  // em pessoas inteiras: o item mede leitura de setor, não divisão com resto. E 360 fica
  // de fora: com total 360 o ângulo vale o mesmo número que a fatia, e o distrator
  // "respondeu o ângulo" viraria a própria chave.
  // A faixa é larga de propósito: com totais pequenos a fatia é um número MENOR que o
  // ângulo, com totais grandes é maior, e é isso que espalha a chave pelas cinco letras.
  const total = rng.pick([120, 144, 240, 288, 480, 600, 720]);

  // Quatro ângulos que somam 360, cada um múltiplo de 15 e nenhum menor que 45 — abaixo
  // disso a fatia fica fina demais para carregar rótulo e ser comparada a olho.
  let angulos: number[] = [];
  for (let tentativa = 0; tentativa < 60 && angulos.length === 0; tentativa++) {
    const a = rng.shuffle([45, 60, 75, 90, 105, 120, 135]).slice(0, 3);
    const resto = 360 - a.reduce((s, v) => s + v, 0);
    if (resto >= 45 && resto <= 150 && resto % 15 === 0 && !a.includes(resto)) {
      angulos = rng.shuffle([...a, resto]);
    }
  }
  if (angulos.length === 0) angulos = [90, 90, 90, 90];

  const partes = angulos.map((g) => (g / 360) * total);
  const podem = [0, 1, 2, 3].filter((i) => angulos[i] !== 90);
  return {
    contexto: rng.int(0, CONTEXTOS.length - 1),
    total,
    partes,
    alvo: podem.length > 0 ? (rng.pick(podem) as number) : 0,
  };
}

const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    // O erro que dá nome ao item: ler o ângulo como se fosse a porcentagem.
    error: SET_ANGULO_COMO_PCT,
    priority: 1,
    solve: (p) => Math.round((anguloDe(p, p.alvo) / 100) * p.total),
  },
  // --- faixa 2: cinco candidatos para tres vagas ------------------------------
  //
  // A composicao desta faixa e deliberada. As alternativas numericas sao ordenadas em
  // ordem crescente (regra do catalogo), entao a LETRA da chave e decidida por quantos
  // distratores caem abaixo dela. Com tres candidatos so, os tres entravam sempre, e a
  // chave ficava na mesma letra em nove de cada dez variantes. Com cinco candidatos --
  // uns sempre acima da chave, outros sempre abaixo, outros que dependem da variante --
  // o sorteio da faixa espalha a chave pelas cinco letras.
  {
    error: SET_MEIA_VOLTA,
    priority: 2,
    solve: (p) => Math.round((anguloDe(p, p.alvo) / 180) * p.total),
  },
  {
    error: SET_DIVIDE_POR_100,
    priority: 2,
    solve: (p) => Math.round((anguloDe(p, p.alvo) / 360) * 100),
  },
  { error: SET_RESPONDE_O_ANGULO, priority: 2, solve: (p) => Math.round(anguloDe(p, p.alvo)) },
  { error: SET_TOTAL_POR_FATIA, priority: 2, solve: (p) => Math.round(p.total / 4) },
  { error: SET_FATIA_VIZINHA, priority: 2, solve: (p) => parte(p, (p.alvo + 1) % 4) },
  // --- faixa 3: reposição ------------------------------------------------------
  { error: SET_COMPLEMENTO, priority: 3, solve: (p) => p.total - parte(p, p.alvo) },
];

const EXPLICA: Explicacoes<Params, number> = {
  [SET_ANGULO_COMO_PCT]: (p, v) => [
    txt(`${n0(anguloDe(p, p.alvo))} é a medida do ângulo em GRAUS, não uma porcentagem. `),
    txt(
      `Tomando-a como ${n0(anguloDe(p, p.alvo))}% de ${n0(p.total)}, dá ${conta(n0(v), unidadeDe(p))} — `,
    ),
    txt('mas o círculo inteiro tem 360°, e não 100°.'),
  ],
  [SET_MEIA_VOLTA]: (p, v) => [
    txt(
      `Dividir por 180 dá ${n0(anguloDe(p, p.alvo))} ÷ 180 × ${n0(p.total)} = ${conta(n0(v), unidadeDe(p))}. `,
    ),
    txt(
      'Mas 180° é meia volta: o gráfico inteiro, que representa todo mundo, dá a volta completa, 360°.',
    ),
  ],
  [SET_DIVIDE_POR_100]: (p, v) => [
    txt(`${fracaoDoCirculo(anguloDe(p, p.alvo))}, e isso vezes 100 dá ${n0(v)}%. `),
    txt(
      `Mas a pergunta é ${quantos(unidadeDe(p)).toString().toLowerCase()}, e a porcentagem ainda precisa ser aplicada ao total: `,
    ),
    txt(`${conta(n0(v), unidadeDe(p))} seria a resposta só se o total fosse 100.`),
  ],
  [SET_FATIA_VIZINHA]: (p, v) => [
    txt(
      `${conta(n0(v), unidadeDe(p))} é o tamanho de ${CONTEXTOS[p.contexto]?.nomes[(p.alvo + 1) % 4]}, `,
    ),
    txt(`a fatia ao lado — ela mede ${n0(anguloDe(p, (p.alvo + 1) % 4))}°. `),
    txt(
      `A pergunta é sobre ${CONTEXTOS[p.contexto]?.nomes[p.alvo]}, de ${n0(anguloDe(p, p.alvo))}°.`,
    ),
  ],
  [SET_COMPLEMENTO]: (p, v) => [
    txt(`${conta(n0(v), unidadeDe(p))} é quanto sobra: o total menos a fatia perguntada. `),
    txt(`A pergunta é sobre ${CONTEXTOS[p.contexto]?.nomes[p.alvo]}, e não sobre o resto.`),
  ],
  [SET_RESPONDE_O_ANGULO]: (p, v) => [
    txt(`${n0(v)}° é a medida do ângulo — é o dado do enunciado, não a resposta. `),
    txt(`Respondê-lo é dizer ${conta(n0(v), unidadeDe(p))}, e o que se pede é `),
    txt(
      `${quantos(unidadeDe(p)).toString().toLowerCase()} essa fatia representa dos ${n0(p.total)}.`,
    ),
  ],
  [SET_TOTAL_POR_FATIA]: (p, v) => [
    txt(
      `${n0(p.total)} ÷ 4 = ${conta(n0(v), unidadeDe(p))} valeria se as quatro fatias fossem iguais. `,
    ),
    txt(`Elas não são: esta mede ${n0(anguloDe(p, p.alvo))}°, e um quarto do círculo seriam 90°.`),
  ],
};

export const setorAngulo: ItemTemplate<Params, number> = {
  id: templateId('h1-34-K01-setor-angulo'),
  version: 1,
  taskModel: taskModelCode('H1.34.K01'),
  skills: [skillCode('H1.34')],
  axis: 'I',
  level: 'N1',
  title: 'Ler um gráfico de setores pelo ângulo',
  responseModel: '3PL',
  difficultySources: ['steps.2', 'two-supports'],
  params: drawParams,
  solve: (p) => parte(p, p.alvo),
  buggy: comExplicacoes(BUGGY, EXPLICA),
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.total % 360 !== 0) fontes.push('non-round-numbers');
    if (anguloDe(p, p.alvo) === 90 || anguloDe(p, p.alvo) === 180) fontes.push('steps.1');
    return fontes;
  },
  validate(_b, p) {
    if (p.partes.some((v) => !Number.isInteger(v))) return 'fatia com número de pessoas quebrado';
    if (new Set(p.partes).size < 4) return 'duas fatias do mesmo tamanho';
    if (anguloDe(p, p.alvo) === 90) return 'o setor perguntado é um quarto exato do círculo';
    return null;
  },
  build(p, key, distractors, rng) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const fatias: Fatia[] = ctx.nomes.map((nome, i) => ({ nome, valor: parte(p, i) }));
    const nome = ctx.nomes[p.alvo] as string;
    const ang = anguloDe(p, p.alvo);

    const cena = setores({
      fatias,
      destaque: p.alvo,
      rotulo: 'nada',
      anguloNoDestaque: true,
      title: ctx.titulo,
      desc: describeSetores(fatias, ctx.titulo.toLowerCase(), true),
    });

    return {
      stem: [
        txt(`${ctx.texto} Ao todo, responderam `),
        strong(`${conta(n0(p.total), ctx.conta)}`),
        txt('. O gráfico abaixo mostra o resultado; o setor de '),
        strong(nome),
        txt(` mede `),
        strong(`${n0(ang)}°`),
        txt('.'),
      ],
      comando: [txt(`${quantos(ctx.conta)} escolheram ${nome}?`)],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'setores',
          scene: cena,
          alt: cena.a11y.desc,
        },
      ],
      interaction: assembleSelectOne<number>({
        structure: 'E2',
        key,
        distractors,
        rng,
        render: (v): RichText => [txt(`${conta(n0(v), ctx.conta)}`)],
      }),
      dicas: [
        [
          txt('O círculo inteiro representa '),
          strong('todo mundo'),
          txt(` — ${osN(ctx.conta, n0(p.total))} — e dá a volta completa: 360°.`),
        ],
        [
          txt(`Então a fatia está para 360° como a parte está para ${n0(p.total)}: `),
          txt(`parte = ${n0(ang)} ÷ 360 × ${n0(p.total)}.`),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(`O círculo inteiro vale ${conta(n0(p.total), ctx.conta)} e mede 360°. `),
        txt(`A fatia de ${nome} mede ${n0(ang)}°, isto é, ${fracaoDoCirculo(ang)} do círculo. `),
        txt(
          `Logo ela vale ${n0(ang)} ÷ 360 × ${n0(p.total)} = ${conta(n0(parte(p, p.alvo)), ctx.conta)}.`,
        ),
      ],
      declared: 'media',
    };
  },
};
